import React, { useEffect, useRef } from 'react';

const ScrapsPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrapTypes = [
    {
      title: "Talon/Wire Scraps",
      icon: "fas fa-coil",
      description: "High-quality aluminium wire and talon scraps for recycling applications",
      features: ["Clean wire scraps", "Sorted by grade", "Minimal contamination", "Ready for processing"],
      applications: ["Wire rod production", "Casting applications", "Alloy manufacturing", "Secondary smelting"]
    },
    {
      title: "UBC (Used Beverage Cans)",
      icon: "fas fa-can-food",
      description: "Collected and sorted used aluminium beverage cans with high recycling value",
      features: ["Food-grade aluminium", "High recycling value", "Consistent quality", "Environmentally beneficial"],
      applications: ["New can production", "Sheet manufacturing", "Automotive parts", "Construction materials"]
    },
    {
      title: "Turnings",
      icon: "fas fa-cog",
      description: "Aluminium turnings from machining and manufacturing processes",
      features: ["Machining waste", "Various alloy grades", "Clean processing", "Size classification"],
      applications: ["Foundry applications", "Ingot production", "Alloy blending", "Secondary processing"]
    },
    {
      title: "Extrusions",
      icon: "fas fa-industry",
      description: "Aluminium extrusion scraps and off-cuts from manufacturing processes",
      features: ["Profile off-cuts", "Quality extrusions", "Sorted by alloy", "Structural grade material"],
      applications: ["Re-extrusion", "Casting feedstock", "Alloy production", "Specialized applications"]
    }
  ];

  const services = [
    {
      title: "Import Operations",
      icon: "fas fa-ship",
      description: "Global sourcing of quality aluminium scraps from international markets",
      benefits: ["Global supply network", "Quality assurance", "Competitive pricing", "Reliable logistics"]
    },
    {
      title: "Export Services", 
      icon: "fas fa-plane",
      description: "Efficient export of processed aluminium scraps to international buyers",
      benefits: ["International market access", "Documentation support", "Quality certification", "Timely delivery"]
    },
    {
      title: "Quality Sorting",
      icon: "fas fa-sort",
      description: "Professional sorting and grading of aluminium scraps by type and quality",
      benefits: ["Grade classification", "Contamination removal", "Quality standards", "Value optimization"]
    },
    {
      title: "Processing Services",
      icon: "fas fa-tools", 
      description: "Value-added processing including cleaning, cutting, and preparation",
      benefits: ["Custom processing", "Size reduction", "Cleaning services", "Specification matching"]
    }
  ];

  const sustainabilityImpacts = [
    {
      title: "Environmental Impact",
      icon: "fas fa-leaf",
      description: "Reducing waste and promoting circular economy through aluminium recycling",
      impact: "95% energy savings compared to primary production"
    },
    {
      title: "Resource Conservation",
      icon: "fas fa-recycle",
      description: "Conserving natural resources by maximizing aluminium reuse and recycling",
      impact: "Preserving bauxite and reducing mining impact"
    },
    {
      title: "Carbon Footprint",
      icon: "fas fa-globe",
      description: "Significantly reducing carbon emissions through recycling processes",
      impact: "Lower CO2 emissions per ton of recycled aluminium"
    },
    {
      title: "Waste Reduction",
      icon: "fas fa-trash-alt",
      description: "Diverting aluminium waste from landfills into productive use",
      impact: "Zero waste to landfill philosophy"
    }
  ];

  const galleryItems = [
    {
      icon: "fas fa-recycle",
      title: "Sorted Aluminium Scraps",
      subtitle: "Professional grading and sorting"
    },
    {
      icon: "fas fa-ship",
      title: "Import-Export Operations", 
      subtitle: "Global trading network"
    },
    {
      icon: "fas fa-leaf",
      title: "Sustainable Processing",
      subtitle: "Eco-friendly solutions"
    },
    {
      icon: "fas fa-can-food",
      title: "UBC Collection",
      subtitle: "Used beverage cans"
    },
    {
      icon: "fas fa-cog",
      title: "Aluminium Turnings",
      subtitle: "Machining waste material"
    },
    {
      icon: "fas fa-industry",
      title: "Extrusion Scraps",
      subtitle: "Quality structural material"
    }
  ];

  return (
    <div className="scraps-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .scraps-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: #1E2329;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Operations Showcase */
        .operations-showcase {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .showcase-image-container {
          position: relative;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
          margin-bottom: 3rem;
          background: white;
        }

        .showcase-image-container img {
          width: 100%;
          height: 500px;
          object-fit: cover;
          transition: all 0.3s ease;
        }

        .showcase-image-container:hover img {
          transform: scale(1.05);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          color: white;
          padding: 2rem;
          transform: translateY(100%);
          transition: transform 0.4s ease;
        }

        .showcase-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        .image-overlay h3 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .image-overlay p {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .showcase-image-placeholder {
          width: 100%;
          height: 500px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
        }

        .image-placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .image-placeholder-content i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .image-placeholder-text {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .image-placeholder-desc {
          font-size: 1rem;
          opacity: 0.8;
          max-width: 400px;
        }

        /* Gallery Section */
        .gallery-section {
          background: white;
          padding: 100px 0;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
        }

        .gallery-item {
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
        }

        .gallery-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .gallery-item:hover {
          transform: translateY(-10px);
          border-color: rgba(123, 63, 228, 0.3);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .gallery-image-placeholder {
          width: 100%;
          height: 280px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 2px dashed #7B3FE4;
          transition: all 0.3s ease;
        }

        .gallery-item:hover .gallery-image-placeholder {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        .gallery-image-placeholder .image-placeholder-content i {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }

        .gallery-image-placeholder .image-placeholder-text {
          font-size: 1rem;
          font-weight: 500;
        }

        .gallery-caption {
          padding: 2rem;
          text-align: center;
        }

        .gallery-caption h4 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 0.5rem;
        }

        .gallery-caption p {
          color: #4A5260;
          font-size: 1rem;
        }

        /* Scrap Types Section */
        .scrap-types-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .scrap-types-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
        }

        .scrap-type-card {
          background: white;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          transition: all 0.5s ease;
          position: relative;
        }

        .scrap-type-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 5px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .scrap-type-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.12);
        }

        .scrap-type-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          min-height: 350px;
        }

        .scrap-type-content {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .scrap-type-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .scrap-type-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          transition: all 0.3s ease;
        }

        .scrap-type-card:hover .scrap-type-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .scrap-type-icon i {
          font-size: 2rem;
          color: white;
        }

        .scrap-type-header h3 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1E2329;
        }

        .scrap-type-description {
          color: #4A5260;
          margin-bottom: 2rem;
          line-height: 1.7;
          font-size: 1.1rem;
        }

        .scrap-type-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .detail-section h4 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #7B3FE4;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-list {
          list-style: none;
        }

        .detail-list li {
          padding: 0.4rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .detail-list li::before {
          content: '▶';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-size: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
        }

        .scrap-type-media {
          position: relative;
          overflow: hidden;
          background: #F7FAFC;
        }

        .scrap-image-placeholder {
          width: 100%;
          height: 100%;
          min-height: 350px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px dashed #7B3FE4;
          transition: all 0.3s ease;
        }

        .scrap-type-card:hover .scrap-image-placeholder {
          border-color: #9A60FF;
          background: rgba(123, 63, 228, 0.05);
        }

        /* Reverse layout for even cards */
        .scrap-type-card:nth-child(even) .scrap-type-layout {
          grid-template-columns: 1fr 1fr;
        }

        .scrap-type-card:nth-child(even) .scrap-type-media {
          order: 2;
        }

        .scrap-type-card:nth-child(even) .scrap-type-content {
          order: 1;
        }

        /* Services Section */
        .services-section {
          padding: 100px 0;
          background: white;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
        }

        .service-card {
          background: white;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .service-card:hover {
          transform: translateY(-10px);
          border-color: rgba(123, 63, 228, 0.3);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .service-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .service-card:hover .service-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .service-icon i {
          font-size: 2rem;
          color: white;
        }

        .service-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .service-description {
          color: #4A5260;
          margin-bottom: 1.5rem;
          line-height: 1.7;
        }

        .service-benefits {
          list-style: none;
          text-align: left;
        }

        .service-benefits li {
          padding: 0.5rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .service-benefits li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
        }

        /* Sustainability Section */
        .sustainability-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .sustainability-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
        }

        .sustainability-card {
          background: white;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .sustainability-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #14B87A, #10a36a);
        }

        .sustainability-card:hover {
          border-color: #14B87A;
          transform: translateY(-8px);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .sustainability-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #14B87A, #10a36a);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .sustainability-card:hover .sustainability-icon {
          transform: scale(1.1);
        }

        .sustainability-icon i {
          font-size: 2rem;
          color: white;
        }

        .sustainability-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .sustainability-card p {
          color: #4A5260;
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .impact-highlight {
          background: #F6F7F9;
          padding: 1rem;
          border-radius: 10px;
          font-size: 0.9rem;
          color: #14B87A;
          font-weight: 600;
          border: 1px solid #E8F5E8;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #4A5260 100%);
          text-align: center;
          color: white;
        }

        .cta-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-block;
          padding: 18px 40px;
          background: white;
          color: #7B3FE4;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.4s ease;
          cursor: pointer;
          border: none;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3);
        }

        .cta-button-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-button-secondary:hover {
          background: white;
          color: #7B3FE4;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }
          
          .hero-subtitle {
            font-size: 1.1rem;
          }
          
          .section-header h2 {
            font-size: 2.2rem;
          }
          
          .container {
            padding: 0 1rem;
          }
          
          .scrap-type-layout {
            grid-template-columns: 1fr;
          }

          .scrap-type-card:nth-child(even) .scrap-type-layout {
            grid-template-columns: 1fr;
          }

          .scrap-type-card:nth-child(even) .scrap-type-media,
          .scrap-type-card:nth-child(even) .scrap-type-content {
            order: unset;
          }

          .scrap-type-content {
            padding: 2rem;
          }
          
          .scrap-type-details {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }
          
          .gallery-grid {
            grid-template-columns: 1fr;
          }

          .showcase-image-placeholder {
            height: 300px;
          }

          .image-placeholder-content i {
            font-size: 3rem;
          }

          .image-placeholder-text {
            font-size: 1.1rem;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section - Only background drone image */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-recycle"></i>
          </div>
          <h1 className="animate-fade-in">Aluminium Scraps Trading</h1>
          <p className="hero-subtitle animate-fade-in">
            Comprehensive aluminium scrap trading services with global import-export operations for sustainable recycling solutions
          </p>
        </div>
      </section>





      {/* Scrap Types Section */}
      <section className="scrap-types-section">
        <div className="container">
          <div className="section-header">
            <h2>Scrap Types</h2>
            <p>Comprehensive range of aluminium scrap materials for various recycling and manufacturing applications</p>
          </div>
          <div className="scrap-types-grid">
            {scrapTypes.map((type, index) => (
              <div key={index} className="scrap-type-card animate-on-scroll">
                <div className="scrap-type-layout">
                  <div className="scrap-type-media">
                    <img
                      src={index === 0 ? "/images/scraps/aluminium-turning-scrap-1675840995285.jpg" :
                           index === 1 ? "/images/scraps/aluminum-ubc-scrap.jpg" :
                           index === 2 ? "/images/scraps/aluminium-turning-scrap-1675840995285.jpg" :
                           "/images/scraps/aluminium-extrusion-scrap.jpg"}
                      alt={type.title}
                      loading="lazy"
                      style={{width: '100%', height: '100%', minHeight: '400px', objectFit: 'cover'}}
                    />
                  </div>
                  <div className="scrap-type-content">
                    <div className="scrap-type-header">
                      <div className="scrap-type-icon">
                        <i className={type.icon}></i>
                      </div>
                      <h3>{type.title}</h3>
                    </div>
                    <p className="scrap-type-description">{type.description}</p>
                    <div className="scrap-type-details">
                      <div className="detail-section">
                        <h4>Features</h4>
                        <ul className="detail-list">
                          {type.features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h4>Applications</h4>
                        <ul className="detail-list">
                          {type.applications.map((app, i) => (
                            <li key={i}>{app}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive trading services covering the complete aluminium scrap value chain</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card animate-on-scroll">
                <div className="service-icon">
                  <i className={service.icon}></i>
                </div>
                <h3>{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-benefits">
                  {service.benefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="sustainability-section">
        <div className="container">
          <div className="section-header">
            <h2>Sustainability Impact</h2>
            <p>Contributing to environmental conservation through responsible aluminium recycling practices</p>
          </div>
          <div className="sustainability-grid">
            {sustainabilityImpacts.map((impact, index) => (
              <div key={index} className="sustainability-card animate-on-scroll">
                <div className="sustainability-icon">
                  <i className={impact.icon}></i>
                </div>
                <h3>{impact.title}</h3>
                <p>{impact.description}</p>
                <div className="impact-highlight">{impact.impact}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Partner with Sustainable Trading</h2>
            <p>
              Join us in creating a sustainable future through responsible aluminium scrap trading. Contact us for competitive pricing, quality materials, and reliable global service.
            </p>
            <div className="cta-buttons">
              <button className="cta-button" onClick={() => window.location.href = '/contact/enquiry-form'}>
                Get Quote
              </button>
              <button className="cta-button cta-button-secondary" onClick={() => window.location.href = '/businesses'}>
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScrapsPage;